import React, { memo, useMemo } from "react";
import styles from "../llm-summary.module.scss";

function randomBetween(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

interface SkeletonProps {
  variant: "paragraph" | "list";
}

const PARAGRAPH_SKELETON = {
  captionWidth: 30,
  paragraphWidth: 95,
  paragraphHeight: 60,
  borderRadius: 4
};

const LIST_SKELETON = {
  captionWidth: 30,
  borderRadius: 4
};

const LLMSummarySkeleton: React.FC<SkeletonProps> = ({ variant }) => {
  const numberOfListBlocks = useMemo(() => randomBetween(3, 4), []);
  const listBlocks = useMemo(
    () =>
      Array.from({ length: numberOfListBlocks }).map((_, idx) => ({
        width: 80 - idx * randomBetween(3, 6),
        height: 14,
        borderRadius: 4,
        marginLeft: 10
      })),
    [numberOfListBlocks]
  );

  if (variant === "paragraph") {
    return (
      <section
        className={styles.llm_summary_skeleton}
        aria-label="AI Summary loading"
      >
        <div
          className={styles.llm_summary_skeleton_item}
          style={{
            width: `${PARAGRAPH_SKELETON.captionWidth}%`,
            height: 20,
            marginBottom: 8
          }}
          aria-hidden="true"
        />
        <div
          className={styles.llm_summary_skeleton_item}
          style={{
            width: `${PARAGRAPH_SKELETON.paragraphWidth}%`,
            height: PARAGRAPH_SKELETON.paragraphHeight,
            borderRadius: PARAGRAPH_SKELETON.borderRadius,
            marginBottom: 14
          }}
          aria-hidden="true"
        />
      </section>
    );
  }

  return (
    <section
      className={styles.llm_summary_skeleton}
      aria-label="AI Summary loading"
    >
      <div
        className={styles.llm_summary_skeleton_item}
        style={{
          width: `${LIST_SKELETON.captionWidth}%`,
          height: 20,
          marginBottom: 8
        }}
        aria-hidden="true"
      />

      <ul
        className={styles.llm_summary_content}
        style={{ paddingLeft: 0, margin: 0 }}
      >
        {listBlocks.map((block, idx) => (
          <li key={idx}>
            <div
              className={styles.llm_summary_skeleton_item}
              style={{
                width: `${block.width}%`,
                height: block.height,
                borderRadius: block.borderRadius,
                marginBottom: 8,
                marginLeft: block.marginLeft
              }}
              aria-hidden="true"
            />
          </li>
        ))}
      </ul>
      <div
        className={styles.llm_summary_skeleton_item}
        style={{
          width: `${LIST_SKELETON.captionWidth}%`,
          height: 20,
          marginBottom: 8
        }}
        aria-hidden="true"
      />

      <ul
        className={styles.llm_summary_content}
        style={{ paddingLeft: 0, margin: 0 }}
      >
        {listBlocks.map((block, idx) => (
          <li key={idx}>
            <div
              className={styles.llm_summary_skeleton_item}
              style={{
                width: `${block.width}%`,
                height: block.height,
                borderRadius: block.borderRadius,
                marginBottom: 8,
                marginLeft: block.marginLeft
              }}
              aria-hidden="true"
            />
          </li>
        ))}
      </ul>
    </section>
  );
};

export default memo(LLMSummarySkeleton);
